import { useEffect, useRef, useState } from 'react'
import { useField } from 'formik'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Instagram, TikTok, YouTube } from 'blaze-ui/icons/20'
import { BrandPlanTactic } from 'types'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { getTacticInfo } from 'components/TacticIntegrationList/utils'
import { AccountsWithToggle } from 'screens/Schedule/EditChannelsModal/AccountsWithToggle'
import { NewAccountButton } from 'screens/Schedule/EditChannelsModal/NewAccountButton'
import { usePostTypeField } from 'screens/Schedule/EditChannelsModal/usePostTypeField'
import { CROSSPOST_RELEVANT_TACTICS } from 'screens/Schedule/EditChannelsModal/Types'
import { ChannelPill, ChannelDef } from 'screens/VirtualMarketerOnboarding/V4ChannelSelection/ChannelPill'
import Styles from '../AiAvatarAnnouncementModal.module.scss'

const VIDEO_TACTICS = CROSSPOST_RELEVANT_TACTICS.video_crosspost_contents

const VIDEO_CHANNELS: ChannelDef[] = [
  { id: 'ig-reel', label: 'Instagram Reels', icon: <Instagram size={20} />, tactic: BrandPlanTactic.InstagramReel },
  { id: 'tiktok', label: 'TikTok', icon: <TikTok size={20} />, tactic: BrandPlanTactic.TikTok },
  { id: 'youtube', label: 'YouTube', icon: <YouTube size={20} />, tactic: BrandPlanTactic.YouTube },
]

const providerFor = (tactic: BrandPlanTactic | null) => (tactic ? getTacticInfo(tactic)?.integrationType : undefined)

export const AvatarVideoAccounts = () => {
  const [isCustomizing, setIsCustomizing] = useState(false)
  const [field] = usePostTypeField({ name: 'video_crosspost_contents', postType: 'post', contentType: 'avatar_video' })
  const [accountIdsField, , { setValue: setAccountIds }] = useField<number[] | undefined>(`${field.name}.account_ids`)
  const [, , { setValue: setTactics }] = useField<BrandPlanTactic[] | undefined>(`${field.name}.tactics`)
  const accountIds = accountIdsField.value ?? []
  const { data } = useExternalIntegrations()
  const integrations = data?.external_integrations ?? []

  const defaulted = useRef(false)
  useEffect(() => {
    if (defaulted.current || !data) return
    defaulted.current = true

    const connectedChannels = VIDEO_CHANNELS.filter((channel) =>
      data.external_integrations.some((integration) => integration.provider === providerFor(channel.tactic)),
    )
    if (connectedChannels.length === 0) return

    const connectedProviders = connectedChannels.map((channel) => providerFor(channel.tactic))
    setAccountIds(
      data.external_integrations
        .filter((integration) => connectedProviders.includes(integration.provider))
        .map((integration) => integration.id),
    )
    setTactics(
      connectedChannels.map((channel) => channel.tactic).filter((tactic): tactic is BrandPlanTactic => !!tactic),
    )
  }, [data, setAccountIds, setTactics])

  const enabledChannels = VIDEO_CHANNELS.filter((channel) => {
    const provider = providerFor(channel.tactic)
    if (!provider) return false
    const ids = integrations
      .filter((integration) => integration.provider === provider)
      .map((integration) => integration.id)
    return ids.length > 0 && ids.some((id) => accountIds.includes(id))
  })

  const summary =
    enabledChannels.length > 0 ? (
      <div className={Styles.pills}>
        {enabledChannels.map((channel) => (
          <ChannelPill key={channel.id} channel={channel} isSelected={false} readOnly />
        ))}
      </div>
    ) : (
      <Text variant="secondary" color="var(--dark-60)">
        No channels selected yet. Customize to choose where your videos post.
      </Text>
    )

  return (
    <div className={Styles.accounts}>
      <div className={Styles.accountsHeader}>
        <Heading level={5}>Accounts to crosspost to</Heading>
        <Button variant="secondary" size="sm" onPress={() => setIsCustomizing((current) => !current)}>
          {isCustomizing ? 'Done' : 'Customize'}
        </Button>
      </div>
      {isCustomizing ? (
        <>
          <AccountsWithToggle
            tactics={VIDEO_TACTICS}
            contentsFieldName="video_crosspost_contents"
            postType="post"
            contentType="avatar_video"
          />
          <NewAccountButton />
        </>
      ) : (
        summary
      )}
    </div>
  )
}
